import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Table, Button, Form, Input, Space, Row, Col, Select, Tag, Tooltip, DatePicker, message } from "antd";
import { SearchOutlined, SyncOutlined, FilterOutlined, UpOutlined, EyeOutlined } from "@ant-design/icons";
import moment from "moment";

import { searchExpenses } from "../../services/exepenseService";
import { RootState } from '../../app/store';
import './Exepense.scss';

const { RangePicker } = DatePicker;

// Payment method options
const paymentMethodOptions = [
  { value: "Cash", label: "Cash" },
  { value: "Credit Card", label: "Credit Card" },
  { value: "Debit Card", label: "Debit Card" },
  { value: "Online Transfer", label: "Online Transfer" },
  { value: "Others", label: "Others" },
];

// Account type options
const accountTypeOptions = [
  { value: "Bank Account", label: "Bank Account" },
  { value: "Credit Card", label: "Credit Card" },
  { value: "E-Wallet", label: "E-Wallet" },
  { value: "Cash", label: "Cash" },
];

// Expense type options
const expenseTypeOptions = [
  { value: "Fixed", label: "Fixed" },
  { value: "Variable", label: "Variable" },
];

// Currency options
const currencyOptions = [
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
  { value: "VND", label: "VND" },
  { value: "INR", label: "INR" },
  { value: "JPY", label: "JPY" },
];

// Define an interface for the search payload
interface ExpenseSearchPayload {
  page: number;
  limit: number;
  token: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  categoryId?: string;
  paymentMethod?: string;
  accountType?: string;
  expenseType?: string;
  currency?: string;
  searchText?: string;
  tags?: string[];
}

const ExepensePage = () => {
  const { token } = useSelector((state: RootState) => state.auth);
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [expenseList, setExpenseList] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [searchVisible, setSearchVisible] = useState(false);

    useEffect(() => {
        fetchExpenses(1, 10);
        // The initial load intentionally runs once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const fetchExpenses = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const payload = { page, limit, token };
      const response = await searchExpenses(payload);
      
      if (response.success) {
        setExpenseList(response.data.expenses);
        setPagination(response.data.pagination);
      } else {
        message.error(response.message || "Failed to fetch expenses");
      }
    } catch (error) {
      console.error("Failed to fetch expenses:", error);
      message.error("Failed to fetch expenses");
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (values) => {
    const searchPayload: ExpenseSearchPayload = {
      page: 1,
      limit: pagination.itemsPerPage,
      token
    };

    // Add dateRange if provided
    if (values.dateRange && values.dateRange.length === 2) {
      searchPayload.startDate = values.dateRange[0].format('YYYY-MM-DD');
      searchPayload.endDate = values.dateRange[1].format('YYYY-MM-DD');
    }

    // Add amount range if provided
    if (values.minAmount) {
      searchPayload.minAmount = parseFloat(values.minAmount);
    }
    if (values.maxAmount) {
      searchPayload.maxAmount = parseFloat(values.maxAmount);
    }

    // Add other filters
    if (values.userId) searchPayload.userId = values.userId;
    if (values.categoryId) searchPayload.categoryId = values.categoryId;
    if (values.paymentMethod) searchPayload.paymentMethod = values.paymentMethod;
    if (values.accountType) searchPayload.accountType = values.accountType;
    if (values.expenseType) searchPayload.expenseType = values.expenseType;
    if (values.currency) searchPayload.currency = values.currency;
    if (values.searchText) searchPayload.searchText = values.searchText;
    if (values.tags && values.tags.length > 0) searchPayload.tags = values.tags;

    searchExpenses(searchPayload)
      .then((response) => {
        if (response.success) {
          setExpenseList(response.data.expenses);
          setPagination(response.data.pagination);
        } else {
          message.error(response.message || "Search failed");
        }
      })
      .catch((error) => {
        console.error("Search error:", error);
        message.error("Failed to search expenses");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleReset = () => {
    form.resetFields();
    fetchExpenses(1, pagination.itemsPerPage);
  };

  const paginationConfig = {
    current: pagination.currentPage,
    pageSize: pagination.itemsPerPage,
    total: pagination.totalItems,
    onChange: (page, pageSize) => {
      // Get current form values for persistent search
      const values = form.getFieldsValue();
      const searchPayload: ExpenseSearchPayload = {
        page,
        limit: pageSize,
        token
      };

      // Add dateRange if provided
      if (values.dateRange && values.dateRange.length === 2) {
        searchPayload.startDate = values.dateRange[0].format('YYYY-MM-DD');
        searchPayload.endDate = values.dateRange[1].format('YYYY-MM-DD');
      }

      // Add other filters from form
      if (values.userId) searchPayload.userId = values.userId;
      if (values.minAmount) searchPayload.minAmount = parseFloat(values.minAmount);
      if (values.maxAmount) searchPayload.maxAmount = parseFloat(values.maxAmount);
      if (values.categoryId) searchPayload.categoryId = values.categoryId;
      if (values.paymentMethod) searchPayload.paymentMethod = values.paymentMethod;
      if (values.accountType) searchPayload.accountType = values.accountType;
      if (values.expenseType) searchPayload.expenseType = values.expenseType;
      if (values.currency) searchPayload.currency = values.currency;
      if (values.searchText) searchPayload.searchText = values.searchText;
      if (values.tags && values.tags.length > 0) searchPayload.tags = values.tags;

      searchExpenses(searchPayload)
        .then((response) => {
          if (response.success) {
            setExpenseList(response.data.expenses);
            setPagination(response.data.pagination);
          } else {
            message.error(response.message || "Failed to fetch expenses");
          }
        })
        .catch((error) => {
          console.error("Failed to fetch expenses:", error);
          message.error("Failed to fetch expenses");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // Helper function to format color for Tag component
  const formatTagColor = (color) => {
    if (!color) return "#108ee9";
    
    // Remove # if present
    const cleanColor = color.replace('#', '');
    
    // If it's 8 characters, remove the first 2 characters (FF) and keep the last 6
    if (cleanColor.length === 8) {
      return `#${cleanColor.substring(2)}`;
    }
    
    // If it's already 6 characters, return as is
    if (cleanColor.length === 6) {
      return `#${cleanColor}`;
    }
    
    // If it's 3 characters, expand it (e.g., "F00" -> "FF0000")
    if (cleanColor.length === 3) {
      return `#${cleanColor.split('').map(char => char + char).join('')}`;
    }
    
    // Default fallback
    return "#108ee9";
  };

  const columns = [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: 50,
      render: (_, __, index) => (pagination.currentPage - 1) * pagination.itemsPerPage + index + 1
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (date) => moment(date).format("DD/MM/YYYY"),
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix()
    },
    {
      title: "User",
      dataIndex: "userId",
      key: "userId",
      width: 150,
      render: (userId) => userId ? (userId.displayName || userId.username || userId.email) : "N/A"
    },
    {
      title: "Category / Type",
      key: "categoryType",
      width: 180,
      render: (_, record) => {
        const category = record.categoryId;
        const type = category?.transactionType;
        let typeColor = "default";
        let typeShort = "";
        if (type) {
          const t = type.toLowerCase();
          if (t === "expense") { typeColor = "red"; typeShort = "E"; }
          else if (t === "income") { typeColor = "green"; typeShort = "I"; }
          else if (t === "transfer") { typeColor = "blue"; typeShort = "T"; }
          else { typeShort = type[0]?.toUpperCase() || ""; }
        }
        return (
          <span>
            {category ? (
              <Tag color={formatTagColor(category.color)} style={{ marginRight: 6 }}>
                {category.name}
              </Tag>
            ) : "N/A"}
            {typeShort && (
              <Tag color={typeColor} style={{ fontWeight: 600, marginLeft: 0 }}>
                {typeShort}
              </Tag>
            )}
          </span>
        );
      }
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (amount, record) => (
        <span style={{ fontWeight: 'bold' }}>
          {record.currency || ''} {new Intl.NumberFormat('en-US', {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(amount)}
        </span>
      ),
      sorter: (a, b) => a.amount - b.amount
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (createdAt) => createdAt ? moment(createdAt).fromNow() : "N/A"
    },
    {
      title: "Synced At",
      dataIndex: "syncedAt",
      key: "syncedAt",
      width: 120,
      render: (syncedAt) => {
        if (!syncedAt) return <span style={{ color: '#999' }}>Never</span>;
        const now = moment();
        const synced = moment(syncedAt);
        const diffHours = now.diff(synced, 'hours', true);
        const diffDays = now.diff(synced, 'days', true);
        
        let color = undefined;
        if (diffHours < 1) {
          color = '#52c41a'; // Green for recent sync
        } else if (diffHours < 24) {
          color = '#fa8c16'; // Orange for same day
        } else if (diffDays < 7) {
          color = '#722ed1'; // Purple for this week
        } else {
          color = '#ff4d4f'; // Red for old sync
        }
        
        return (
          <Tooltip title={`Last synced: ${synced.format('YYYY-MM-DD HH:mm:ss')}`}>
            <span style={{ color }}>
              {synced.fromNow()}
            </span>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        if (!a.syncedAt && !b.syncedAt) return 0;
        if (!a.syncedAt) return 1;
        if (!b.syncedAt) return -1;
        return moment(b.syncedAt).valueOf() - moment(a.syncedAt).valueOf();
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      width: 160,
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              type="primary" 
              size="small"
              onClick={() => message.info("Expense details view not implemented yet")}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card title="Expense Management" className="expense-page-card">
      <div className="search-container">
        <Button
          type="default"
          icon={searchVisible ? <UpOutlined /> : <FilterOutlined />}
          onClick={() => setSearchVisible(!searchVisible)}
          style={{ marginBottom: 16 }}
        >
          {searchVisible ? "Hide Filters" : "Show Filters"}
        </Button>

        {searchVisible && (
          <Form
            form={form}
            layout="horizontal"
            onFinish={onSearch}
            className="expense-search-form"
            size="small"
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="userId" label="User ID">
                  <Input placeholder="Search by user ID" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="categoryId" label="Category">
                  <Input placeholder="Search by category ID" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="dateRange" label="Date Range">
                  <RangePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="paymentMethod" label="Payment Method">
                  <Select
                    placeholder="Select payment method"
                    options={paymentMethodOptions}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="accountType" label="Account Type">
                  <Select
                    placeholder="Select account type"
                    options={accountTypeOptions}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="expenseType" label="Expense Type">
                  <Select
                    placeholder="Select expense type"
                    options={expenseTypeOptions}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="currency" label="Currency">
                  <Select
                    placeholder="Select currency"
                    options={currencyOptions}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="searchText" label="Search Text">
                  <Input placeholder="Search in description and notes" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Amount Range">
                  <Input.Group compact>
                    <Form.Item name="minAmount" noStyle>
                      <Input
                        style={{ width: '45%' }}
                        placeholder="Min"
                        type="number"
                      />
                    </Form.Item>
                    <Input
                      style={{
                        width: '10%',
                        textAlign: 'center',
                        pointerEvents: 'none',
                        backgroundColor: '#fff',
                      }}
                      placeholder="-"
                      disabled
                    />
                    <Form.Item name="maxAmount" noStyle>
                      <Input
                        style={{ width: '45%' }}
                        placeholder="Max"
                        type="number"
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
                    Search
                  </Button>
                  <Button onClick={handleReset}>
                    Reset
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        )}
      </div>

      <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<SyncOutlined />}
          onClick={() => fetchExpenses(pagination.currentPage, pagination.itemsPerPage)}
        >
          Refresh Data
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={expenseList}
        rowKey="_id"
        loading={loading}
        pagination={paginationConfig}
        size="middle"
        className="expense-table"
      />
    </Card>
  );
};

export default ExepensePage;
